import { textAlignmentMap } from "../../content/configuration";
import { useCurrentSite } from "../../contexts/models/SiteContext";
import DisplayTitle from "../../pages/website/DisplayTitle";
import { Page, Section, Site, WebsiteEvent } from "../../resources/modelTypes";
import { RootState } from "../../resources/reduxTypes";
import {
  selectCollection,
  SelectedCollection,
} from "../../resources/selectors";
import { PageDisplayModes } from "../../website/components/EditDesign/constants";
import { PageSlugs } from "../types";
import { DisplayWebsiteContent } from "../website-display/DisplayWebsiteContent";
import { css } from "@emotion/react";
import { DesignComponent, LargeDivider } from "@minted/fancyclothes";
import { styleUtils } from "@minted/minted-components";
import React, { memo } from "react";
import { connect } from "react-redux";

const scrollTargetStyles = css`
  scroll-margin-top: ${styleUtils.rem(100)};
`;

const wrapperStyles = css`
  max-width: ${styleUtils.rem(975)};
  margin: 0 auto;
  padding-bottom: ${styleUtils.rem(80)};
`;

/*
  "-ms-flex" is used here because we want to override the flex styling
  created by Autoprefixer and as such this way will affect only IE
  browsers, while preserving regular flex for compatibility reasons.
*/
export const flex1 = css`
  -ms-flex: 0 1 auto;
  flex: 1;
`;

export type PageDesignComponentProps = {
  displayMode: string;
  page: Page;
};

type PageDesignComponentPropsWithRedux = {
  sectionCollection: SelectedCollection<Section>;
  eventCollection: SelectedCollection<WebsiteEvent> | null;
  isOnlineInvitation: boolean;
  site: Site;
} & PageDesignComponentProps;

class PageDesignComponent extends DesignComponent {
  props?: PageDesignComponentPropsWithRedux;
  render() {
    if (!this.props) {
      return null;
    }
    const {
      displayMode,
      eventCollection,
      isOnlineInvitation,
      page,
      sectionCollection,
      site,
    } = this.props;

    return (
      <>
        <div css={scrollTargetStyles} id={`page-${page.slug}`}>
          {page.slug !== PageSlugs.Home && !isOnlineInvitation && (
            <>
              {displayMode === PageDisplayModes.multiPage && <LargeDivider />}
              <DisplayTitle
                hideDivider={!page.configuration.showPageDividers}
                title={page.givenName}
              />
            </>
          )}
          <div css={wrapperStyles}>
            <div
              css={[
                flex1,
                textAlignmentMap.get(page.configuration.textAlignment),
              ]}
            >
              <div>
                <DisplayWebsiteContent
                  configuration={page.configuration}
                  eventCollection={eventCollection}
                  page={page}
                  sectionCollection={sectionCollection}
                  site={site}
                />
              </div>
            </div>
          </div>
        </div>
        <LargeDivider />
      </>
    );
  }
}

type PageDesignComponentWithSiteProps = {
  eventCollection: SelectedCollection<WebsiteEvent> | null;
  sectionCollection: SelectedCollection<Section>;
} & PageDesignComponentProps;

const PageDesignComponentWithSite = ({
  displayMode,
  eventCollection,
  page,
  sectionCollection,
}: PageDesignComponentWithSiteProps) => {
  const site = useCurrentSite();

  return (
    // @ts-expect-error Force types for redux/react connection
    <PageDesignComponent
      displayMode={displayMode}
      eventCollection={eventCollection}
      isOnlineInvitation={site.isOnlineInvitation}
      page={page}
      sectionCollection={sectionCollection}
      site={site}
    />
  );
};

const mapStateToProps = (
  state: RootState,
  props: PageDesignComponentProps,
) => ({
  eventCollection:
    props.page.slug === PageSlugs.Events
      ? selectCollection(state.resources.events)
      : null,
  sectionCollection: selectCollection(state.resources.sections, {
    page: props.page.id,
  }),
});

const ConnectedPageDesignComponent = connect(mapStateToProps)(
  PageDesignComponentWithSite,
) as unknown as React.FC<PageDesignComponentProps>;

const MemoizedPageDesignComponent = memo(ConnectedPageDesignComponent);

export { MemoizedPageDesignComponent as PageDesignComponent };
